import styled from "@emotion/styled";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useDeleteConnectorConfigurationMutation } from "ConnectorConfigurations/Api/Mutations/useDeleteConnectorConfigurationMutation";
import { useConnectorConfigurationsQuery } from "ConnectorConfigurations/Api/Queries/useConnectorConfigurationsQuery";
import type { ConnectorConfigurationListItemDto } from "Infrastructure/Api/Api";
import { ConfirmDialog } from "Shared/Components/ConfirmDialog";
import { BlDataTable } from "Shared/DataTable/BlDataTable";
import type { FC } from "react";
import { useState } from "react";

const StyledCell = styled(Box)``;

const StyledTextCell = styled(Box)`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const StyledApiKeyCell = styled(Box)`
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 400px;
	max-width: 600px;
`;

const StyledApiKeyText = styled.span`
	font-family: monospace;
	font-size: 14px;
	word-break: break-all;
	display: inline-block;
`;

const StyledActionsCell = styled(Box)`
	display: flex;
	align-items: center;
	gap: 8px;
	justify-content: flex-end;
`;

type Props = {
	onEdit: (config: ConnectorConfigurationListItemDto) => void;
};

export const ConnectorConfigurationsTable: FC<Props> = ({ onEdit }) => {
	const { data, isLoading } = useConnectorConfigurationsQuery();
	const deleteMutation = useDeleteConnectorConfigurationMutation();
	const [configToDelete, setConfigToDelete] =
		useState<ConnectorConfigurationListItemDto | null>(null);

	const configurations: ConnectorConfigurationListItemDto[] = data?.items ?? [];

	const handleEdit = (config: ConnectorConfigurationListItemDto) => {
		onEdit(config);
	};

	const handleDeleteClick = (config: ConnectorConfigurationListItemDto) => {
		setConfigToDelete(config);
	};

	const handleConfirmDelete = async () => {
		if (configToDelete) {
			await deleteMutation.mutateAsync(configToDelete.connectorConfigurationID);
			setConfigToDelete(null);
		}
	};

	const handleCancelDelete = () => {
		setConfigToDelete(null);
	};

	return (
		<>
			<BlDataTable
				rows={configurations.map((x, index) => ({ data: x }))}
				rowEntityID="connectorConfigurationID"
				isLoading={isLoading}
				getHeaderCell={(column) => <div>{column.label ?? column.id}</div>}
				columns={[
					{ id: "provider" as const, label: "Provider", fillRow: true },
					{ id: "endpoint" as const, label: "Endpoint" },
					{ id: "apiKey" as const, label: "API Key", maxWidth: 600 },
					{
						id: "action" as const,
						label: "Actions",
						maxWidth: 0,
						justifySelf: "flex-end",
					},
				]}
				rowsPerPage={10}
				getCell={(id, data) => {
					switch (id) {
						case "provider":
							return (
								<Tooltip title={data.provider} arrow>
									<StyledTextCell>{data.provider}</StyledTextCell>
								</Tooltip>
							);
						case "endpoint":
							return (
								<Tooltip title={data.endpoint} arrow>
									<StyledTextCell>{data.endpoint}</StyledTextCell>
								</Tooltip>
							);
						case "apiKey":
							// The API never returns the stored key - it can only be overwritten
							return (
								<StyledApiKeyCell>
									<Tooltip title="Stored encrypted, not readable" arrow>
										<StyledApiKeyText>••••••••••••••••••••</StyledApiKeyText>
									</Tooltip>
								</StyledApiKeyCell>
							);
						case "action":
							return (
								<StyledActionsCell>
									<IconButton
										size="small"
										color="primary"
										onClick={(e) => {
											e.stopPropagation();
											handleEdit(data);
										}}
										title="Edit"
									>
										<EditIcon fontSize="small" />
									</IconButton>
									<IconButton
										size="small"
										color="error"
										onClick={(e) => {
											e.stopPropagation();
											handleDeleteClick(data);
										}}
										title="Delete"
									>
										<DeleteIcon fontSize="small" />
									</IconButton>
								</StyledActionsCell>
							);
						default:
							return <StyledCell />;
					}
				}}
			/>
			{configToDelete && (
				<ConfirmDialog
					open={!!configToDelete}
					title="Delete Connector Configuration"
					message={`Are you sure you want to delete the connector configuration for "${configToDelete.provider}" (${configToDelete.endpoint})? This action cannot be undone.`}
					confirmText="Delete"
					onConfirm={handleConfirmDelete}
					onCancel={handleCancelDelete}
					isLoading={deleteMutation.isPending}
				/>
			)}
		</>
	);
};
